/**
 * Onboarding step 4 — ICP & personas. Add 1–3 buyer personas (name,
 * role, pains). Template chips reuse the SAME starter blueprints the
 * Personas tab offers (src/lib/crm/personas.ts) so the vocabulary is
 * identical; on activation each row becomes a real `category: 'persona'`
 * Person record.
 */

import { Plus, Trash2 } from 'lucide-react';
import { PERSONA_TEMPLATES, type PersonaTemplate } from '../../../lib/crm/personas';
import type { DraftPersona, OnboardingDraft } from '../../../lib/onboarding';

const MAX_PERSONAS = 3;

/** Role-shaped starters only — the buying-committee seats are too meta here. */
const STARTERS: PersonaTemplate[] = PERSONA_TEMPLATES.filter(
  (t) => t.group === 'role',
).slice(0, 4);

interface Props {
  draft: OnboardingDraft;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

export function StepPersonas({ draft, onPatch }: Props) {
  const setRow = (idx: number, patch: Partial<DraftPersona>) => {
    const next = draft.personas.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onPatch({ personas: next });
  };

  const addRow = (row: DraftPersona = { name: '', role: '', pains: '' }) => {
    if (draft.personas.length >= MAX_PERSONAS) return;
    onPatch({ personas: [...draft.personas, row] });
  };

  const removeRow = (idx: number) =>
    onPatch({ personas: draft.personas.filter((_, i) => i !== idx) });

  const applyTemplate = (t: PersonaTemplate) => {
    const row: DraftPersona = {
      name: t.archetype,
      role: t.profile.role ?? '',
      pains: (t.profile.painPoints ?? []).join('; '),
    };
    // Fill the first blank row, otherwise append (respecting the cap).
    const blank = draft.personas.findIndex(
      (p) => !p.name.trim() && !p.role.trim() && !p.pains.trim(),
    );
    if (blank >= 0) {
      setRow(blank, row);
    } else {
      addRow(row);
    }
  };

  return (
    <div className="onb-step">
      <h3 className="t-section-title onb-step__title">ICP &amp; personas</h3>
      <p className="t-meta onb-step__blurb">
        Who do you sell to? Add up to three — drafts, sequences, and scoring
        tune themselves to these people.
      </p>

      <div className="onb-chip-row">
        <span className="t-eyebrow">Start from</span>
        {STARTERS.map((t) => (
          <button
            key={t.id}
            type="button"
            className="glass-btn onb-chip"
            onClick={() => applyTemplate(t)}
            title={t.blurb}
          >
            {t.label}
          </button>
        ))}
      </div>

      {draft.personas.map((p, idx) => (
        <div key={idx} className="onb-repeat-row">
          <div className="onb-row">
            <div style={{ flex: 1 }}>
              <label className="t-eyebrow onb-label" htmlFor={`onb-persona-name-${idx}`}>
                Persona {idx + 1} name
              </label>
              <input
                id={`onb-persona-name-${idx}`}
                className="glass-input"
                value={p.name}
                onChange={(e) => setRow(idx, { name: e.target.value })}
                placeholder="Ops Olivia"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="t-eyebrow onb-label" htmlFor={`onb-persona-role-${idx}`}>
                Role
              </label>
              <input
                id={`onb-persona-role-${idx}`}
                className="glass-input"
                value={p.role}
                onChange={(e) => setRow(idx, { role: e.target.value })}
                placeholder="VP Operations"
              />
            </div>
            {draft.personas.length > 1 && (
              <button
                type="button"
                className="glass-btn onb-remove"
                onClick={() => removeRow(idx)}
                aria-label={`Remove persona ${idx + 1}`}
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
          <label className="t-eyebrow onb-label" htmlFor={`onb-persona-pains-${idx}`}>
            Pains (separate with ;)
          </label>
          <input
            id={`onb-persona-pains-${idx}`}
            className="glass-input"
            value={p.pains}
            onChange={(e) => setRow(idx, { pains: e.target.value })}
            placeholder="Manual reporting; tool sprawl"
          />
        </div>
      ))}

      {draft.personas.length < MAX_PERSONAS && (
        <button type="button" className="glass-btn" onClick={() => addRow()}>
          <Plus size={13} /> Add persona
        </button>
      )}
    </div>
  );
}
